/** Call diagnostics only: never records media, device labels/IDs, SDP, IPs or credentials. */
type Fields = Record<string, unknown>;
type Rum = { recordEvent: (type: string, fields: Fields) => void };
type Context = {
  lessonId?: string; localPeerId?: string; userId?: string; role?: string; turnNodeId?: string;
};
type Expected = { audio?: boolean; video?: boolean; screenSharing?: boolean };
type Call = { peer: string; peerConnection?: RTCPeerConnection };
type Row = {
  at: number; inAudio: number; outAudio: number; inVideo: number; outVideo: number;
  lostAudio: number; lostVideo: number; framesDecoded: number | null; framesEncoded: number | null;
  audioEnergy: number | null; audioDuration: number | null; audioLevel: number | null;
  sourceAudioLevel: number | null; jitterMs: number | null; concealed: number;
  connection: string; ice: string; signaling: string; route: Fields | null;
  senders: Fields[]; receivers: Fields[]; playback: Fields[];
};
type Entry = { attempt: string; started: number; lastSummary: number; previous?: Row;
  audioProgress: number; videoProgress: number; connectionProblemSince?: number; lastIssue: string; issueAt: number; last?: Fields };
const entries = new Map<RTCPeerConnection, Entry>();
const throttles = new Map<string, number>();
let currentContext: (() => Context) | undefined;
let currentOwner: object | undefined;
let stopCurrent: (() => void) | undefined;
let sessionCount = 0;
let budgetStart = Date.now();
const MAX_EVENTS_PER_30_MINUTES = 100;
const PROBLEM_EVENTS = new Set(['capture_error', 'play_rejected', 'pair_observation', 'signaling_error']);
type DiagnosticUser = { _id?: unknown; role?: unknown };
let getDiagnosticUser: (() => DiagnosticUser | null | undefined) | undefined;
/** Resolve the current account at event time, including preview before PeerJS exists. */
export function setCallDiagnosticUserGetter(getter: () => DiagnosticUser | null | undefined): void {
  getDiagnosticUser = getter;
}
const clip = (value: unknown, max = 100) => String(value ?? '').slice(0, max);
const round = (value: number | null) => value === null || !Number.isFinite(value) ? null : Math.round(value * 10000) / 10000;
const numeric = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;

function context(remotePeerId?: string): Fields {
  let value: Context = {};
  try { value = currentContext?.() || {}; } catch {}
  const localPeerId = clip(value.localPeerId);
  return {
    lessonId: clip(value.lessonId), localPeerId, remotePeerId: clip(remotePeerId),
    pairKey: localPeerId && remotePeerId ? [localPeerId, clip(remotePeerId)].sort().join(':') : null,
    userId: clip(value.userId), role: clip(value.role), turnNodeId: clip(value.turnNodeId),
  };
}

export function emitCallDiagnostic(event: string, fields: Fields = {}): boolean {
  if (typeof window === 'undefined' || !PROBLEM_EVENTS.has(event)) return false;
  const rum = (window as unknown as { __pranaClubRum?: Rum }).__pranaClubRum;
  if (!rum?.recordEvent) return false;
  const now = Date.now();
  if (now - budgetStart >= 1800000) { budgetStart = now; sessionCount = 0; }
  if (sessionCount >= MAX_EVENTS_PER_30_MINUTES) return false;
  let user: DiagnosticUser | null | undefined;
  try { user = getDiagnosticUser?.(); } catch {}
  const payload = { schema: 1, event, at: new Date(now).toISOString(), ...fields,
    appUserId: clip(user?._id || fields.userId || 'ANONYMOUS'), appUserRole: clip(user?.role || fields.role || 'USER') };
  // AWS custom event limit is 6 KB; leave room for SDK/platform metadata.
  if (new TextEncoder().encode(JSON.stringify(payload)).length > 5000) return false;
  try { rum.recordEvent('call_diagnostic', payload); sessionCount++; return true; }
  catch { return false; } // Diagnostics must never interrupt a call.
}

function allow(key: string, seconds: number): boolean {
  const now = Date.now();
  if (throttles.has(key) && now - throttles.get(key)! < seconds * 1000) return false;
  throttles.set(key, now);
  // Bound long-lived tabs without storing a growing history of participants.
  if (throttles.size > 250) {
    for (const [k, at] of throttles) if (now - at > 120000) throttles.delete(k);
    if (throttles.size > 250) throttles.delete(throttles.keys().next().value!);
  }
  return true;
}

function playbackState(element: HTMLMediaElement): Fields {
  return {
    paused: element.paused, muted: element.muted, volume: round(element.volume),
    readyState: element.readyState, networkState: element.networkState,
    currentTime: round(element.currentTime), errorCode: element.error?.code || null,
    hasStream: !!element.srcObject,
    outputSelection: 'sinkId' in element ? ((element as HTMLMediaElement & { sinkId: string }).sinkId ? 'selected' : 'default') : 'unsupported',
  };
}

export function recordRemotePlayback(element: HTMLMediaElement, event: 'play_resolved' | 'play_rejected', error?: unknown): void {
  if (event !== 'play_rejected') return;
  const peer = element.getAttribute('data-remote-audio-peer') || '';
  const name = error && typeof error === 'object' && 'name' in error ? clip(error.name, 60) : 'UnknownError';
  if (name === 'AbortError') return;
  if (!allow('play:' + peer + ':' + event + ':' + (event === 'play_rejected' ? name : ''), 60)) return;
  emitCallDiagnostic(event, { ...context(peer), ...playbackState(element),
    ...(event === 'play_rejected' ? { errorName: name } : {}),
    audibility: 'not_measurable_by_browser' });
}

export function recordCaptureFailure(stage: string, error: unknown): void {
  const name = error && typeof error === 'object' && 'name' in error ? clip(error.name, 60) : 'UnknownError';
  if (allow('capture:' + stage + ':' + name, 30))
    emitCallDiagnostic('capture_error', { ...context(), stage: clip(stage), errorName: name });
}

function tracks(list: Array<RTCRtpSender | RTCRtpReceiver>): Fields[] {
  return list.slice(0, 4).map(item => ({ kind: item.track?.kind || null,
    enabled: item.track?.enabled ?? null, muted: item.track?.muted ?? null,
    readyState: item.track?.readyState || 'missing' }));
}

async function read(pc: RTCPeerConnection, peer: string): Promise<Row> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let report: RTCStatsReport;
  try {
    report = await Promise.race([pc.getStats(), new Promise<RTCStatsReport>((_, reject) => {
      timeout = setTimeout(() => reject(new Error('StatsTimeout')), 2000);
    })]);
  } finally { clearTimeout(timeout); }
  const row: Row = { at: Date.now(), inAudio: 0, outAudio: 0, inVideo: 0, outVideo: 0,
    lostAudio: 0, lostVideo: 0, framesDecoded: null, framesEncoded: null,
    audioEnergy: null, audioDuration: null, audioLevel: null, sourceAudioLevel: null,
    jitterMs: null, concealed: 0, connection: pc.connectionState,
    ice: pc.iceConnectionState, signaling: pc.signalingState, route: null,
    senders: tracks(pc.getSenders()), receivers: tracks(pc.getReceivers()), playback: [] };
  let pair: any;
  report.forEach((s: any) => {
    if (s.type === 'transport' && s.selectedCandidatePairId) pair = report.get(s.selectedCandidatePairId);
    if (s.type === 'media-source' && s.kind === 'audio') row.sourceAudioLevel = numeric(s.audioLevel);
    const kind = s.kind || s.mediaType;
    if (s.type === 'inbound-rtp' && !s.isRemote) {
      if (kind === 'audio') {
        row.inAudio += Number(s.packetsReceived) || 0; row.lostAudio += Number(s.packetsLost) || 0;
        row.audioEnergy = numeric(s.totalAudioEnergy); row.audioDuration = numeric(s.totalSamplesDuration);
        row.audioLevel = numeric(s.audioLevel); row.jitterMs = numeric(s.jitter) === null ? null : s.jitter * 1000;
        row.concealed += Number(s.concealedSamples) || 0;
      }
      if (kind === 'video') {
        row.inVideo += Number(s.packetsReceived) || 0; row.lostVideo += Number(s.packetsLost) || 0;
        if (numeric(s.framesDecoded) !== null) row.framesDecoded = (row.framesDecoded || 0) + s.framesDecoded;
      }
    }
    if (s.type === 'outbound-rtp' && !s.isRemote) {
      if (kind === 'audio') row.outAudio += Number(s.packetsSent) || 0;
      if (kind === 'video') { row.outVideo += Number(s.packetsSent) || 0; if (numeric(s.framesEncoded) !== null) row.framesEncoded = (row.framesEncoded || 0) + s.framesEncoded; }
    }
  });
  if (!pair) report.forEach((s: any) => { if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded') pair = s; });
  if (pair) {
    const local = report.get(pair.localCandidateId), remote = report.get(pair.remoteCandidateId);
    row.route = { state: pair.state, localType: local?.candidateType || null,
      remoteType: remote?.candidateType || null, protocol: local?.protocol || null,
      relayProtocol: local?.relayProtocol || null, rttMs: round(numeric(pair.currentRoundTripTime) === null ? null : pair.currentRoundTripTime * 1000) };
  }
  if (typeof document !== 'undefined') {
    row.playback = Array.from(document.querySelectorAll<HTMLAudioElement>('audio[data-remote-audio-peer]'))
      .filter(element => element.getAttribute('data-remote-audio-peer') === peer).slice(0, 2).map(playbackState);
  }
  return row;
}

/** Pure interval analysis. Missing/unsupported stats never become fabricated zeroes. */
export function analyzeInterval(previous: Row | undefined, row: Row): Fields {
  const delta = (key: keyof Row) => {
    if (!previous || typeof row[key] !== 'number' || typeof previous[key] !== 'number') return null;
    const value = (row[key] as number) - (previous[key] as number);
    return value < 0 ? null : value;
  };
  const energy = delta('audioEnergy'), duration = delta('audioDuration');
  return {
    intervalMs: previous ? row.at - previous.at : null,
    receivedAudioPackets: delta('inAudio'), sentAudioPackets: delta('outAudio'),
    receivedVideoPackets: delta('inVideo'), sentVideoPackets: delta('outVideo'),
    decodedFrames: delta('framesDecoded'), encodedFrames: delta('framesEncoded'),
    lostAudioPackets: delta('lostAudio'), lostVideoPackets: delta('lostVideo'),
    concealedSamples: delta('concealed'),
    receivedAudioRms: round(energy !== null && duration !== null && duration > 0 ? Math.sqrt(energy / duration) : null),
  };
}

export function startCallTelemetry(
  getCalls: () => Call[], owner: any, getContext: () => Context,
  getExpected: (peer: string) => Expected,
): void {
  if (typeof window === 'undefined' || currentOwner === owner) return;
  stopCurrent?.();
  currentOwner = owner; currentContext = getContext;
  let stopped = false, busy = false;
  const owned = new Set<RTCPeerConnection>();
  const removers: Array<() => void> = [];
  const diagnostic = (event: string, fields: Fields) => {
    if (currentOwner === owner) emitCallDiagnostic(event, fields);
  };
  const tick = async () => {
    if (stopped || busy || currentOwner !== owner) return;
    busy = true;
    try {
      const live = new Set<RTCPeerConnection>();
      const calls = getCalls().filter(call => call?.peerConnection).slice(0, 20);
      await Promise.all(calls.map(async call => {
        const pc = call.peerConnection!; live.add(pc); owned.add(pc);
        let entry = entries.get(pc);
        if (!entry) {
          const now = Date.now();
          entry = { attempt: crypto.randomUUID(), started: now, lastSummary: now,
            audioProgress: now, videoProgress: now, lastIssue: '', issueAt: 0 };
          entry.last = { ...context(call.peer), attemptId: entry.attempt };
          entries.set(pc, entry);
          diagnostic('pair_started', { ...context(call.peer), attemptId: entry.attempt,
            userAgent: navigator.userAgent.slice(0, 300),
            hardwareConcurrency: navigator.hardwareConcurrency || null });
        }
        try {
          const row = await read(pc, call.peer);
          if (stopped || currentOwner !== owner) return;
          const interval = analyzeInterval(entry.previous, row);
          const expected = getExpected(call.peer);
          if (!entry.previous || (interval.receivedAudioPackets as number) > 0 || row.inAudio < entry.previous.inAudio || !expected.audio) entry.audioProgress = row.at;
          if (!entry.previous || (row.framesDecoded === null ? (interval.receivedVideoPackets as number) > 0 : (interval.decodedFrames as number) > 0) || (row.framesDecoded !== null && entry.previous.framesDecoded !== null && row.framesDecoded < entry.previous.framesDecoded) || !expected.video || expected.screenSharing) entry.videoProgress = row.at;
          const issues: string[] = [];
          const disconnected = row.connection === 'disconnected' || row.ice === 'disconnected';
          if (disconnected) entry.connectionProblemSince ??= row.at;
          else entry.connectionProblemSince = undefined;
          if (row.connection === 'failed' || row.ice === 'failed' ||
            (disconnected && row.at - entry.connectionProblemSince! >= 15000))
            issues.push('connection:' + row.connection + '/' + row.ice);
          const connected = ['connected','completed'].includes(row.ice) || row.connection === 'connected';
          if (connected && expected.audio && row.at - entry.audioProgress >= 15000) issues.push('audio:rtp-idle');
          if (connected && expected.video && !expected.screenSharing && row.at - entry.videoProgress >= 15000) issues.push(row.framesDecoded === null ? 'video:rtp-idle' : 'video:decode-idle');
          const fields = { ...context(call.peer), attemptId: entry.attempt, elapsedMs: row.at - entry.started,
            ...interval, expected, connection: row.connection, ice: row.ice, signaling: row.signaling,
            sourceAudioLevel: round(row.sourceAudioLevel), receivedAudioLevel: round(row.audioLevel),
            jitterMs: round(row.jitterMs), route: row.route, senders: row.senders, receivers: row.receivers,
            playback: row.playback, hidden: document.hidden, issues };
          entry.last = fields;
          const signature = issues.join('|');
          if (signature && signature !== entry.lastIssue && row.at - entry.issueAt >= 60000) {
            diagnostic('pair_observation', fields); entry.issueAt = row.at;
          } else if (!signature && entry.lastIssue) diagnostic('pair_recovered', fields);
          entry.lastIssue = signature;
          if (row.at - entry.lastSummary >= 300000) { diagnostic('pair_summary', fields); entry.lastSummary = row.at; }
          entry.previous = row;
        } catch {
          if (!stopped && currentOwner === owner && allow('stats:' + entry.attempt, 120))
            diagnostic('stats_unavailable', { ...context(call.peer), attemptId: entry.attempt });
        }
      }));
      for (const pc of owned) if (!live.has(pc)) {
        const entry = entries.get(pc);
        if (entry) diagnostic('pair_ended', { ...entry.last, attemptId: entry.attempt });
        entries.delete(pc); owned.delete(pc);
      }
    } finally { busy = false; }
  };
  const stop = () => {
    if (stopped) return; stopped = true; clearInterval(timer);
    removers.forEach(remove => remove());
    for (const pc of owned) {
      const entry = entries.get(pc);
      if (entry) diagnostic('pair_ended', { ...entry.last, attemptId: entry.attempt });
      entries.delete(pc);
    }
    owned.clear();
    if (currentOwner === owner) { currentOwner = undefined; currentContext = undefined; stopCurrent = undefined; }
  };
  const onPageHide = () => {
    for (const pc of owned) {
      const entry = entries.get(pc);
      if (entry?.last) diagnostic('pair_pagehide', entry.last);
    }
  };
  const timer = setInterval(() => { void tick().catch(() => {}); }, 5000);
  stopCurrent = stop;
  const onSignalingError = (error: { type?: unknown }) => {
    if (allow('signaling:' + String(error?.type), 30))
      diagnostic('signaling_error', { ...context(), errorType: clip(error?.type, 60) });
  };
  owner.on?.('error', onSignalingError);
  removers.push(() => owner.off?.('error', onSignalingError));
  owner.on?.('close', stop);
  window.addEventListener('pagehide', onPageHide);
  removers.push(() => window.removeEventListener('pagehide', onPageHide), () => owner.off?.('close', stop));
  void tick().catch(() => {});
}
